/* eslint-disable jsx-a11y/no-onchange */
import { useState } from "react"
import PropTypes from "prop-types"
import AttendeeRestrictionSelector from "components/admin/AttendeeRestrictionSelector"
import { Pane, StatusMessage } from "components/ui"
import { StackView } from "@planningcenter/tapestry-react"
import AreaFormAges from "./AreaFormAges"
import AreaFormGrades from "./AreaFormGrades"
import { stringify, get, isEqual, isBlank, isPresent, arrayToObject } from "utils/helpers"
import moment from "moment-timezone"

const getToString = (area, value, defaultValue = "") => {
  return stringify(get(area, value), defaultValue)
}

const getGradeRestriction = area => {
  return isPresent(area) && isEqual(area.minGrade, area.maxGrade) ? "only" : "between"
}

const getAgeRestriction = area => {
  if (isBlank(area.minAge) && isPresent(area.maxAge)) {
    return "atMost"
  } else if (isPresent(area.minAge) && isBlank(area.maxAge)) {
    return "atLeast"
  } else if (isPresent(area) && isEqual(area.minAge, area.maxAge)) {
    return "exactly"
  } else {
    return "between"
  }
}

const AreaForm = ({
  onCancel,
  onSave,
  gradeOptions,
  genderOptions,
  eventStartDate,
  area = {},
  signupFeatures
}) => {
  const [saveStatus, setSaveStatus] = useState(null)
  const [validation, setValidation] = useState({})

  const [attendeeRestriction, setAttendeeRestriction] = useState(
    get(area, "hasAttendeeTypeRestriction", false)
  )
  const [attendeeRestrictions, setAttendeeRestrictions] = useState(
    arrayToObject(area.attendeeTypeRestrictions || [])
  )

  const [name, setName] = useState(getToString(area, "name"))
  const [capacity, setCapacity] = useState(getToString(area, "capacity"))
  const [evaluateAgeAt, setEvaluateAgeAt] = useState(area?.evaluateAgeAt || eventStartDate)
  const [minAge, setMinAge] = useState(getToString(area, "minAge"))
  const [maxAge, setMaxAge] = useState(getToString(area, "maxAge"))
  const [ageUnit, setAgeUnit] = useState(getToString(area, "ageRestrictionUnit", "years"))
  const [minGrade, setMinGrade] = useState(getToString(area, "minGrade", "-2"))
  const [maxGrade, setMaxGrade] = useState(getToString(area, "maxGrade", "-2"))
  const [gender, setGender] = useState(getToString(area, "gender", "all"))
  const [restriction, setRestriction] = useState(
    getToString(area, "restrictionType", "none").toLowerCase()
  )
  const [gradeRestrictionType, setGradeRestrictionType] = useState(getGradeRestriction(area))
  const [ageRestrictionType, setAgeRestrictionType] = useState(getAgeRestriction(area))

  const validateForm = () => {
    let validation = {}

    if (isBlank(name)) {
      validation.name = "Name can not be blank"
    }

    if (!isBlank(name) && name.length > 255) {
      validation.name = "Name can not be longer than 255 characters"
    }

    if (isBlank(capacity)) {
      validation.capacity = "Capacity must be a number"
    }

    if (
      isEqual(restriction, "grade") &&
      isEqual(gradeRestrictionType, "between") &&
      !isEqual(minGrade, "-2") &&
      !isEqual(maxGrade, "-2")
    ) {
      if (Number(minGrade) > Number(maxGrade)) {
        validation.minGrade = "Minimum grade must be less than or equal to maximum grade"
      }
    }

    if (
      isEqual(restriction, "age") &&
      isEqual(ageRestrictionType, "between") &&
      isPresent(minAge) &&
      isPresent(maxAge)
    ) {
      if (Number(minAge) > Number(maxAge)) {
        validation.minAge = "Minimum age must be less than or equal to maximum age"
      }
    }

    setValidation(validation)
    return validation
  }

  const handleAttendeeRestrictionChange = e => {
    setAttendeeRestriction(!attendeeRestriction)

    const newAttendeeRestrictions = {}

    Object.entries(attendeeRestrictions).map(([key, value]) => {
      newAttendeeRestrictions[key] = {
        ...value,
        isAvailable: e.target.value === "true" ? false : true
      }
    })

    setAttendeeRestrictions(newAttendeeRestrictions)
  }

  const handleCheckboxChange = e => {
    const selectedAttendee = attendeeRestrictions[e.target.value]

    setAttendeeRestrictions({
      ...attendeeRestrictions,
      [selectedAttendee.id]: {
        ...selectedAttendee,
        isAvailable: !selectedAttendee.isAvailable
      }
    })
  }

  const handleSave = () => {
    const validate = validateForm()
    if (isPresent(validate)) {
      return
    }

    setSaveStatus({
      text: "Saving...",
      icon: "spinner"
    })

    let gradeParams = {}
    let ageParams = {}

    if (isEqual(restriction, "grade")) {
      if (isEqual(gradeRestrictionType, "only") && !isEqual(minGrade, "-2")) {
        gradeParams = { min_grade: minGrade, max_grade: minGrade }
      } else {
        gradeParams = {
          ...(!isEqual(minGrade, "-2") && { min_grade: minGrade }),
          ...(!isEqual(maxGrade, "-2") && { max_grade: maxGrade })
        }
      }
    }

    if (isEqual(restriction, "age")) {
      if (isEqual(ageRestrictionType, "exactly")) {
        ageParams = {
          evaluate_age_at: evaluateAgeAt,
          min_age: minAge,
          max_age: minAge,
          age_restriction_unit: ageUnit
        }
      } else {
        ageParams = {
          evaluate_age_at: evaluateAgeAt,
          age_restriction_unit: ageUnit,
          min_age: isEqual(ageRestrictionType, "atMost") ? null : minAge,
          max_age: isEqual(ageRestrictionType, "atLeast") ? null : maxAge
        }
      }
    }

    return onSave({
      name,
      capacity,
      ...(attendeeRestriction && {
        visible_to_attendee_type_ids: Object.values(attendeeRestrictions)
          .filter(x => x.isAvailable)
          .map(x => x.id)
      }),
      gender: isEqual(gender, "all") ? "" : gender,
      restriction_type: restriction,
      ...gradeParams,
      ...ageParams
    })
      .then(_resp => {
        setSaveStatus({
          text: "Saved!",
          icon: "general#check",
          type: "success"
        })
        setTimeout(() => setSaveStatus(null), 2000)
      })
      .catch(error =>
        setSaveStatus({
          text: error.errors || "Changes not saved",
          icon: "general#exclamation-triangle",
          type: "error"
        })
      )
  }

  return (
    <div className="d-f@lg">
      <div className="pr-2@lg f-2">
        <Pane marginBottom={4}>
          <Pane.Header>
            <Pane.Title>Assignment area</Pane.Title>
          </Pane.Header>
          <Pane.Body>
            <StackView axis="horizontal" spacing={2}>
              <div className="f-2">
                <label htmlFor="name">Name</label>
                <input
                  className={`${validation.name && "error-input"}`}
                  type="text"
                  name="name"
                  placeholder="Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
                {validation.name && <div className="c-danger font-size-5">{validation.name}</div>}
              </div>
              <div className="f-1">
                <label htmlFor="capacity">Capacity</label>
                <input
                  className={`${validation.capacity && "error-input"}`}
                  type="number"
                  name="capacity"
                  placeholder="Capacity"
                  value={capacity}
                  onChange={e => setCapacity(e.target.value)}
                />
                <div
                  className={`font-size-5 pt-4p ${
                    validation.capacity ? "c-danger" : "c-neutral-600"
                  }`}
                >
                  Areas are required to have a maximum capacity
                </div>
              </div>
            </StackView>
          </Pane.Body>
        </Pane>
      </div>

      <div className="pl-2@lg f-1">
        <Pane>
          <Pane.Header>
            <Pane.Title>Options</Pane.Title>
          </Pane.Header>
          <Pane.Body>
            <label htmlFor="gender">Available to</label>
            {signupFeatures.includes("attendees") && (
              <>
                <select
                  className="select mb-1"
                  name="gender"
                  value={gender}
                  onChange={e => setGender(e.target.value)}
                >
                  <option value={"all"}>All genders</option>
                  {Object.entries(genderOptions).map(([k, v]) => (
                    <option key={v} value={v}>
                      {k}
                    </option>
                  ))}
                </select>

                <select
                  className="select mb-1"
                  name="restriction"
                  value={restriction}
                  onChange={e => setRestriction(e.target.value)}
                >
                  <option value={"none"}>All ages</option>
                  <option value={"grade"}>Grades</option>
                  <option value={"age"}>Ages</option>
                </select>
              </>
            )}

            {isEqual(restriction, "grade") && (
              <AreaFormGrades
                gradeOptions={gradeOptions}
                gradeRestrictionType={gradeRestrictionType}
                minGrade={minGrade}
                maxGrade={maxGrade}
                onGradeRestrictionChange={setGradeRestrictionType}
                onMinGradeChange={setMinGrade}
                onMaxGradeChange={setMaxGrade}
                validation={validation}
              />
            )}

            {isEqual(restriction, "age") && (
              <AreaFormAges
                ageRestrictionType={ageRestrictionType}
                onAgeRestrictionChange={setAgeRestrictionType}
                minAge={minAge}
                onMinAgeChange={setMinAge}
                maxAge={maxAge}
                onMaxAgeChange={setMaxAge}
                ageUnit={ageUnit}
                onAgeUnitChange={setAgeUnit}
                evaluateAgeAt={evaluateAgeAt}
                onEvaluateAgeChange={newDate =>
                  setEvaluateAgeAt(
                    moment(newDate)
                      .startOf("day")
                      .format()
                  )
                }
                validation={validation}
              />
            )}
            <AttendeeRestrictionSelector
              attendeeTypes={Object.values(attendeeRestrictions)}
              onCheckboxChange={handleCheckboxChange}
              hasAttendeeRestriction={attendeeRestriction}
              onAttendeeRestrictionChange={handleAttendeeRestrictionChange}
            />
          </Pane.Body>
        </Pane>

        <StackView axis="horizontal" distribution="end" spacing={1} marginTop={2}>
          {saveStatus && (
            <div className="mr-a">
              <StatusMessage {...saveStatus} />
            </div>
          )}
          {onCancel && (
            <button className="quiet-btn btn2" onClick={() => onCancel()}>
              Cancel
            </button>
          )}
          <button className="create-btn btn2" type="submit" onClick={handleSave}>
            Save
          </button>
        </StackView>
      </div>
    </div>
  )
}

AreaForm.propTypes = {
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  gradeOptions: PropTypes.shape({}),
  genderOptions: PropTypes.shape({}),
  area: PropTypes.shape({}),
  eventStartDate: PropTypes.string,
  signupFeatures: PropTypes.array
}

export default AreaForm
